import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';

const SmartEscrowComponent = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [form, setForm] = useState({
    seller_email: '', amount: '', currency: 'CUALCOIN',
    item_description: '', conditions: []
  });
  const [conditionDraft, setConditionDraft] = useState({ type: 'deadline', hours: 72, auto_action: 'refund', description: '', percentage: 50, required: 2, min_rating: 3.0 });

  useEffect(() => { fetchContracts(); }, []);

  const fetchContracts = async () => {
    try {
      const res = await axios.get(`${API}/smart-escrow/my-contracts`);
      setContracts(res.data.contracts || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const addCondition = () => {
    const c = { ...conditionDraft };
    setForm(prev => ({ ...prev, conditions: [...prev.conditions, c] }));
    setConditionDraft({ type: 'deadline', hours: 72, auto_action: 'refund', description: '', percentage: 50, required: 2, min_rating: 3.0 });
  };

  const removeCondition = (idx) => {
    setForm(prev => ({ ...prev, conditions: prev.conditions.filter((_, i) => i !== idx) }));
  };

  const createContract = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      await axios.post(`${API}/smart-escrow/create`, {
        ...form, amount: parseFloat(form.amount)
      });
      alert('Smart Contract creado exitosamente');
      setShowCreate(false);
      setForm({ seller_email: '', amount: '', currency: 'CUALCOIN', item_description: '', conditions: [] });
      fetchContracts();
    } catch (err) { alert(err.response?.data?.detail || 'Error al crear contrato'); }
    finally { setProcessing(false); }
  };

  const fulfillMilestone = async (contractId, condId) => {
    if (!window.confirm('Confirmar que este hito se cumplio?')) return;
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/smart-escrow/fulfill-milestone/${contractId}/${condId}`);
      alert(res.data.message);
      fetchContracts();
      if (selectedContract) {
        const updated = await axios.get(`${API}/smart-escrow/contract/${contractId}`);
        setSelectedContract(updated.data.contract);
      }
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setProcessing(false); }
  };

  const addConfirmation = async (contractId, condId) => {
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/smart-escrow/confirm/${contractId}/${condId}`);
      alert(res.data.message);
      fetchContracts();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setProcessing(false); }
  };

  const checkDeadlines = async () => {
    try {
      const res = await axios.post(`${API}/smart-escrow/check-deadlines`);
      alert(res.data.message);
      fetchContracts();
    } catch (err) { alert('Error al verificar deadlines'); }
  };

  const statusColor = (s) => {
    if (s === 'active') return 'bg-blue-100 text-blue-700';
    if (s === 'completed') return 'bg-green-100 text-green-700';
    if (s === 'auto_executed') return 'bg-amber-100 text-amber-700';
    return 'bg-gray-100 text-gray-600';
  };

  const condIcon = (type) => {
    if (type === 'deadline') return 'fa-clock';
    if (type === 'milestone') return 'fa-flag-checkered';
    if (type === 'multi_confirm') return 'fa-users';
    if (type === 'minimum_rating') return 'fa-star';
    return 'fa-cog';
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Cargando contratos...</div>;

  return (
    <div className="space-y-6" data-testid="smart-escrow-module">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Smart Contracts</h2>
          <p className="text-sm text-gray-500">Escrow con condiciones programables y auto-ejecucion</p>
        </div>
        <div className="flex gap-2">
          <button onClick={checkDeadlines} data-testid="check-deadlines-btn"
            className="bg-amber-100 text-amber-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-amber-200 transition-colors">
            <i className="fa fa-clock mr-1"></i>Verificar Deadlines
          </button>
          <button onClick={() => setShowCreate(!showCreate)} data-testid="create-smart-escrow-btn"
            className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:from-indigo-700 hover:to-purple-700 transition-all">
            {showCreate ? 'Cancelar' : '+ Nuevo Contrato'}
          </button>
        </div>
      </div>

      {/* How it works */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-indigo-700 mb-2">Smart Contracts = Escrow Inteligente</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-indigo-600">
          <div className="bg-white/60 rounded-lg p-2 text-center">
            <i className="fa fa-clock text-lg mb-1 block"></i>
            <strong>Deadline</strong><br/>Auto-refund/release al expirar
          </div>
          <div className="bg-white/60 rounded-lg p-2 text-center">
            <i className="fa fa-flag-checkered text-lg mb-1 block"></i>
            <strong>Hitos</strong><br/>Liberacion parcial por etapas
          </div>
          <div className="bg-white/60 rounded-lg p-2 text-center">
            <i className="fa fa-users text-lg mb-1 block"></i>
            <strong>Multi-Confirm</strong><br/>Requiere N confirmaciones
          </div>
          <div className="bg-white/60 rounded-lg p-2 text-center">
            <i className="fa fa-star text-lg mb-1 block"></i>
            <strong>Rating Min</strong><br/>Vendedor con rating minimo
          </div>
        </div>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="bg-white rounded-xl shadow-lg border border-indigo-100 p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Crear Smart Contract</h3>
          <form onSubmit={createContract} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Vendedor</label>
                <input type="email" required value={form.seller_email} onChange={e => setForm({...form, seller_email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500" placeholder="vendedor@email.com" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monto</label>
                <input type="number" step="0.01" required value={form.amount} onChange={e => setForm({...form, amount: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500" placeholder="100.00" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Moneda</label>
                <select value={form.currency} onChange={e => setForm({...form, currency: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="CUALCOIN">CUALcoin</option>
                  <option value="CUAL-USD">CUAL-USD</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripcion</label>
              <input type="text" value={form.item_description} onChange={e => setForm({...form, item_description: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Desarrollo de app, entrega de mercancia..." />
            </div>

            {/* Condition builder */}
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <h4 className="text-sm font-semibold text-gray-700">Condiciones del Contrato</h4>
              
              {form.conditions.length > 0 && (
                <div className="space-y-2">
                  {form.conditions.map((c, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-200">
                      <div className="flex items-center gap-2">
                        <i className={`fa ${condIcon(c.type)} text-indigo-500`}></i>
                        <span className="text-sm">
                          {c.type === 'deadline' && `Deadline: ${c.hours}h (auto-${c.auto_action})`}
                          {c.type === 'milestone' && `Hito: ${c.description || 'Sin desc.'} (${c.percentage}%)`}
                          {c.type === 'multi_confirm' && `Multi-Confirmacion: ${c.required} partes`}
                          {c.type === 'minimum_rating' && `Rating minimo: ${c.min_rating}`}
                        </span>
                      </div>
                      <button type="button" onClick={() => removeCondition(idx)} className="text-red-400 hover:text-red-600 text-xs">
                        <i className="fa fa-times"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap items-end gap-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Tipo</label>
                  <select value={conditionDraft.type} onChange={e => setConditionDraft({...conditionDraft, type: e.target.value})}
                    className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm">
                    <option value="deadline">Deadline</option>
                    <option value="milestone">Hito</option>
                    <option value="multi_confirm">Multi-Confirm</option>
                    <option value="minimum_rating">Rating Min</option>
                  </select>
                </div>
                
                {conditionDraft.type === 'deadline' && (
                  <>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Horas</label>
                      <input type="number" value={conditionDraft.hours} onChange={e => setConditionDraft({...conditionDraft, hours: parseInt(e.target.value)})}
                        className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Si expira</label>
                      <select value={conditionDraft.auto_action} onChange={e => setConditionDraft({...conditionDraft, auto_action: e.target.value})}
                        className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm">
                        <option value="refund">Reembolsar</option>
                        <option value="release">Liberar</option>
                      </select>
                    </div>
                  </>
                )}
                {conditionDraft.type === 'milestone' && (
                  <>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Descripcion</label>
                      <input type="text" value={conditionDraft.description} onChange={e => setConditionDraft({...conditionDraft, description: e.target.value})}
                        className="w-40 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" placeholder="Entrega parcial..." />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">% a liberar</label>
                      <input type="number" min="1" max="100" value={conditionDraft.percentage} onChange={e => setConditionDraft({...conditionDraft, percentage: parseInt(e.target.value)})}
                        className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
                    </div>
                  </>
                )}
                {conditionDraft.type === 'multi_confirm' && (
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Confirmaciones</label>
                    <input type="number" min="2" value={conditionDraft.required} onChange={e => setConditionDraft({...conditionDraft, required: parseInt(e.target.value)})}
                      className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
                  </div>
                )}
                {conditionDraft.type === 'minimum_rating' && (
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Rating min</label>
                    <input type="number" step="0.5" min="1" max="5" value={conditionDraft.min_rating} onChange={e => setConditionDraft({...conditionDraft, min_rating: parseFloat(e.target.value)})}
                      className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
                  </div>
                )}
                <button type="button" onClick={addCondition} data-testid="add-condition-btn"
                  className="bg-indigo-100 text-indigo-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-indigo-200">
                  + Agregar
                </button>
              </div>
            </div>

            <button type="submit" disabled={processing} data-testid="submit-smart-contract-btn"
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-2.5 rounded-lg font-medium hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 transition-all">
              {processing ? 'Creando...' : 'Crear Smart Contract'}
            </button>
          </form>
        </div>
      )}

      {/* Contracts list */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-purple-50">
          <h3 className="font-semibold text-gray-900">Mis Smart Contracts ({contracts.length})</h3>
        </div>

        {contracts.length === 0 ? (
          <div className="p-12 text-center">
            <i className="fa fa-file-contract text-4xl text-gray-300 mb-3 block"></i>
            <p className="text-gray-500">No tienes contratos inteligentes</p>
            <p className="text-xs text-gray-400">Crea uno para transacciones P2P con condiciones automaticas</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {contracts.map(c => (
              <div key={c.id} className="p-5 hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setSelectedContract(selectedContract?.id === c.id ? null : c)}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.role === 'buyer' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                        {c.role === 'buyer' ? 'Comprador' : 'Vendedor'}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(c.status)}`}>{c.status}</span>
                      {c.auto_executed && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Auto</span>}
                    </div>
                    <p className="text-lg font-semibold text-gray-900">{c.amount} {c.currency}</p>
                    <p className="text-sm text-gray-600">{c.role === 'buyer' ? c.seller_email : c.buyer_email}</p>
                    {c.item_description && <p className="text-xs text-gray-400 mt-0.5">"{c.item_description}"</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">{c.conditions?.length || 0} condicion(es)</p>
                    <p className="text-xs text-gray-400">{c.released_amount || 0}/{c.amount} liberado</p>
                    {c.released_amount > 0 && c.released_amount < c.amount && (
                      <div className="w-24 h-1.5 bg-gray-200 rounded-full mt-1">
                        <div className="h-1.5 bg-indigo-500 rounded-full" style={{width: `${(c.released_amount / c.amount) * 100}%`}}></div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Expanded conditions */}
                {selectedContract?.id === c.id && (
                  <div className="mt-4 space-y-2" onClick={e => e.stopPropagation()}>
                    <h4 className="text-sm font-semibold text-gray-700">Condiciones:</h4>
                    {c.conditions?.map(cond => (
                      <div key={cond.id} className={`flex items-center justify-between p-3 rounded-lg border ${cond.fulfilled ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`}>
                        <div className="flex items-center gap-2">
                          <i className={`fa ${condIcon(cond.type)} ${cond.fulfilled ? 'text-green-500' : 'text-gray-400'}`}></i>
                          <div>
                            <p className="text-sm font-medium text-gray-800">{cond.description}</p>
                            {cond.type === 'deadline' && !cond.fulfilled && (
                              <p className="text-xs text-gray-400">Expira: {new Date(cond.deadline).toLocaleString('es-ES')}</p>
                            )}
                            {cond.type === 'multi_confirm' && (
                              <p className="text-xs text-gray-400">{cond.confirmations?.length || 0}/{cond.required_confirmations} confirmaciones</p>
                            )}
                            {cond.fulfilled && <p className="text-xs text-green-600">Cumplido {cond.fulfilled_at ? new Date(cond.fulfilled_at).toLocaleString('es-ES') : ''}</p>}
                          </div>
                        </div>
                        {!cond.fulfilled && c.status === 'active' && (
                          <div>
                            {cond.type === 'milestone' && c.role === 'buyer' && (
                              <button onClick={() => fulfillMilestone(c.id, cond.id)} disabled={processing} data-testid={`fulfill-${cond.id}`}
                                className="bg-green-100 text-green-700 px-3 py-1 rounded-lg text-xs font-medium hover:bg-green-200 disabled:opacity-50">
                                Confirmar Hito
                              </button>
                            )}
                            {cond.type === 'multi_confirm' && (
                              <button onClick={() => addConfirmation(c.id, cond.id)} disabled={processing} data-testid={`confirm-${cond.id}`}
                                className="bg-blue-100 text-blue-700 px-3 py-1 rounded-lg text-xs font-medium hover:bg-blue-200 disabled:opacity-50">
                                Confirmar
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SmartEscrowComponent;
